/**
 * データ取込フォーマット仕様
 * バックエンド側の apps/api/src/routes/imports.ts のスキーマと同期している必要あり
 */

export type ColumnSpec = {
  name: string;
  required: boolean;
  type: '文字列' | '整数' | '小数' | '日付' | '真偽(0/1)';
  description: string;
  example: string;
  notes?: string;
};

export type DataTypeSpec = {
  key: string;
  label: string;
  /** 'data' = 取引データ (売上、納品、諸経費など)、'master' = マスタ (店舗、メニュー、閾値など) */
  category: 'data' | 'master';
  summary: string;
  columns: ColumnSpec[];
  commonNotes: string[];
  /** rollback API 対応可否 (v0.1.3): マスタ系は source_file_id 列未装備のため false。UI で警告表示に使用 */
  rollbackSupported?: boolean;
  /** v0.2 コードキー化 (2026-07-31): CSV列名が破壊的に変わったデータ種別向けの常時警告文言。
   *  折り畳み式の仕様表示だけでは旧テンプレート利用者が変更に気づかない (Design HIGH 反映)。 */
  formatChangeNotice?: string;
};

const commonNotes = [
  '文字コードは UTF-8（BOM有無どちらでも可）',
  '区切り文字はカンマ、改行は CRLF/LF どちらでも可',
  '1行目はヘッダ行（列名）必須',
  '日付は yyyy/mm/dd または yyyy-mm-dd 形式',
  '金額は整数（円）で入力（税込／税抜の区分は各データ種別ごとに異なるので注意）',
];

export const DATA_TYPE_SPECS: Record<string, DataTypeSpec> = {
  sales: {
    key: 'sales',
    label: '売上データ',
    category: 'data',
    rollbackSupported: true,
    summary: '店舗×メニュー×月次の売上明細。ジャーナル（食券機レシート）からの転記を想定。**月内でメニュー変更があった場合は必ず期間を分けて入力**（例：1/1〜1/15と1/16〜1/31の2行に分ける）',
    columns: [
      { name: 'store_code',      required: true,  type: '文字列', description: '店舗コード',       example: '120', notes: 'store_master に登録されているコード' },
      { name: 'menu_code',       required: true,  type: '文字列', description: 'メニューコード',   example: '1',   notes: 'menu_master に登録されているコード。バージョンは period_start 時点で有効なものを自動選択' },
      { name: 'sales_month',     required: true,  type: '日付',   description: '対象月（月初日）', example: '2026/04/01', notes: '対象月の1日を指定' },
      { name: 'period_start',    required: true,  type: '日付',   description: '集計期間開始日',   example: '2026/04/01', notes: 'この日付時点で有効な menu バージョンが自動選択される' },
      { name: 'period_end',      required: true,  type: '日付',   description: '集計期間終了日',   example: '2026/04/30', notes: 'この日付が menu の有効期間終了日を超えるとエラー（期間を分けて再取込）' },
      { name: 'quantity',        required: true,  type: '整数',   description: '販売数量',         example: '618', notes: '0以上の整数' },
      { name: 'sales_amount',    required: true,  type: '整数',   description: '売上金額（税込）', example: '803400', notes: '税込金額。税抜換算はシステムが自動計算' },
    ],
    commonNotes: [
      ...commonNotes,
      '**メニュー変更対応**：月の途中でメニューが変わった場合、period_start / period_end を分けて2行以上に分割してください。例：1/1〜1/15と1/16〜1/31',
      '**単価スナップショット**：sales_details には取込時点の unit_price / tax_rate が保存されるため、後から menu_master を変更しても過去の売上金額に影響しません',
    ],
  },
  menus: {
    key: 'menus',
    label: 'メニューマスタ',
    category: 'master',
    rollbackSupported: false,
    summary: '商品（メニュー）マスタ。単価・税率・メインメニューフラグ等を登録',
    columns: [
      { name: 'store_code',        required: true,  type: '文字列', description: '店舗コード',       example: '120' },
      { name: 'menu_code',         required: true,  type: '文字列', description: 'メニューコード',   example: '1', notes: '店舗内でユニーク' },
      { name: 'menu_name',         required: true,  type: '文字列', description: 'メニュー名',       example: 'ハラミラーメン（醤油）' },
      { name: 'menu_category_id',  required: false, type: '文字列', description: 'カテゴリID',       example: 'MC-10', notes: 'MC-10=ラーメン, MC-20=ライス, MC-30=トッピング, MC-40=一品料理' },
      { name: 'unit_price',        required: true,  type: '整数',   description: '販売単価（税込）', example: '1300' },
      { name: 'tax_rate',          required: false, type: '小数',   description: '税率（0.10=10%）', example: '0.10', notes: '未指定時は 0.10（10%）' },
      { name: 'is_main_menu',      required: false, type: '真偽(0/1)', description: '来客数計上対象', example: '1', notes: '1=来客数にカウント（主にラーメン）、0=カウント対象外' },
      { name: 'effective_from',    required: true,  type: '日付',   description: '有効開始日',       example: '2024/04/01' },
      { name: 'effective_to',      required: false, type: '日付',   description: '有効終了日',       example: '2026/12/31', notes: '未指定時は無期限' },
    ],
    commonNotes,
  },
  delivery_details: {
    key: 'delivery_details',
    label: '納品明細',
    category: 'data',
    rollbackSupported: true,
    formatChangeNotice: '2026-07-31 に列名が変わりました。「食材名」列は廃止され「食材コード (ingredient_code)」列が必須になりました。以前の形式のCSVはエラーになります。お手元のテンプレートが古い場合は「テンプレートCSVをダウンロード」から取得し直してください。食材コードは食材マスタ (CSV取込または `/data` 画面) で確認できます。',
    summary: '食材の納品履歴（原価データ）。業者からの納品書を転記',
    columns: [
      { name: 'store_code',         required: true,  type: '文字列', description: '店舗コード',       example: '120' },
      { name: 'delivery_date',      required: true,  type: '日付',   description: '納品日',           example: '2026/04/15' },
      { name: 'vendor_name',        required: false, type: '文字列', description: '業者名',           example: '本田商店' },
      { name: 'delivery_slip_no',   required: false, type: '文字列', description: '納品書番号',       example: '25113075' },
      { name: 'ingredient_code',    required: true,  type: '文字列', description: '食材コード',       example: 'ING-120-001', notes: 'ingredient_master に登録されているコード (v0.2〜: 食材名ではなくコードで指定)' },
      { name: 'unit_price',         required: false, type: '小数',   description: '単価',             example: '220' },
      { name: 'quantity',           required: false, type: '小数',   description: '数量',             example: '30' },
      { name: 'amount',             required: true,  type: '整数',   description: '金額（税抜）',     example: '6600', notes: '税抜金額' },
      { name: 'tax_rate',           required: false, type: '小数',   description: '税率（0.08=軽減）',example: '0.08', notes: '軽減税率対象=0.08、標準=0.10' },
      { name: 'remarks',            required: false, type: '文字列', description: '備考',             example: '要冷蔵' },
    ],
    commonNotes,
  },
  delivery_sales: {
    key: 'delivery_sales',
    label: 'デリバリー売上',
    category: 'data',
    rollbackSupported: true,
    formatChangeNotice: '2026-07-31 に列名が変わりました。「業者名」列は廃止され「業者コード (service_code)」列が必須になりました。以前の形式のCSVはエラーになります。お手元のテンプレートが古い場合は「テンプレートCSVをダウンロード」から取得し直してください。業者コードはデリバリー業者マスタ (/masters/delivery-services) で確認できます。',
    summary: 'デリバリーサービス（Uber Eats／出前館／menu 等）別の月次売上',
    columns: [
      { name: 'store_code',   required: true,  type: '文字列', description: '店舗コード',                   example: '120' },
      { name: 'service_code', required: true,  type: '文字列', description: 'デリバリー業者コード',         example: 'SVC-120-01', notes: 'delivery_service_master に登録されているコード (v0.2〜: 業者名ではなくコードで指定)' },
      { name: 'sales_month',  required: true,  type: '日付',   description: '対象月（月初日）',             example: '2026/04/01' },
      { name: 'amount',       required: true,  type: '整数',   description: '売上金額（税込）',             example: '250315' },
      { name: 'tax_rate',     required: false, type: '小数',   description: '税率（0.10=10%）',            example: '0.10' },
    ],
    commonNotes,
  },
  labor: {
    key: 'labor',
    label: '人件費',
    category: 'data',
    rollbackSupported: true,
    summary: '店舗別月次人件費（税抜）。v0.1では店舗別必須',
    columns: [
      { name: 'store_code',        required: true, type: '文字列', description: '店舗コード',              example: '120', notes: '会社合算しか手元にない場合は店舗別に按分してから取り込むこと' },
      { name: 'target_month',      required: true, type: '日付',   description: '対象月（月初日）',        example: '2026/04/01' },
      { name: 'labor_cost_amount', required: true, type: '整数',   description: '人件費金額（税抜、円）',  example: '2138630' },
    ],
    commonNotes,
  },
  opex: {
    key: 'opex',
    label: '諸経費',
    category: 'data',
    rollbackSupported: true,
    summary: '店舗別月次諸経費（賃料・電気・ガス・水道・通信・保険 等）',
    columns: [
      { name: 'store_code',    required: true,  type: '文字列', description: '店舗コード',   example: '120' },
      { name: 'target_month',  required: true,  type: '日付',   description: '対象月（月初日）', example: '2025/12/01' },
      { name: 'item_type',     required: true,  type: '文字列', description: '費目種別（rent/electricity/gas/water/telephone/insurance/other）', example: 'rent' },
      { name: 'item_name',     required: false, type: '文字列', description: '費目名（任意）', example: '賃料' },
      { name: 'amount',        required: true,  type: '整数',   description: '金額（税抜、円）', example: '577500' },
      { name: 'tax_rate',      required: false, type: '小数',   description: '税率（0.10=10%）', example: '0.10' },
    ],
    commonNotes,
  },
  kpi_thresholds: {
    key: 'kpi_thresholds',
    label: 'KPI閾値',
    category: 'master',
    rollbackSupported: false,
    summary: '店舗別×KPI種別×閾値の設定。ダッシュボードでの警告表示に使う',
    columns: [
      { name: 'store_code',      required: true, type: '文字列', description: '店舗コード',                  example: '120' },
      { name: 'kpi_type',        required: true, type: '文字列', description: 'KPI種別',                      example: 'cost_rate', notes: 'cost_rate/gross_margin/labor_rate/opex_rate/sales/customer_count/avg_price のいずれか' },
      { name: 'threshold_value', required: true, type: '小数',   description: '閾値（率は0.37=37%）',        example: '0.37', notes: '率の場合は小数、金額の場合は整数' },
      { name: 'direction',       required: true, type: '文字列', description: '方向（above=超過警告／below=未達警告）', example: 'above' },
      { name: 'effective_from',  required: true, type: '日付',   description: '有効開始日',                   example: '2026/04/01' },
    ],
    commonNotes,
  },
  stores: {
    key: 'stores',
    label: '店舗マスタ',
    category: 'master',
    formatChangeNotice: '2026-08-03 に「操作種別 (operation_type)」列が追加されました (末尾任意列、既存の6列CSVはそのまま動作します)。operation_type=renew は既存店舗を閉店し、同じ店舗コードで別店舗として新規登録し直します (店舗IDが変わります)。operation_type=close は閉店、reopen は直近に閉店した店舗の再開を表します。未指定時は continue (実態継続、従来通りの動作) として扱われます。',
    summary: '店舗マスタ。(tenant_id, store_code, effective_from) をキーに upsert (同キーがあれば更新、無ければ履歴追加)',
    columns: [
      { name: 'store_code',     required: true,  type: '文字列', description: '店舗コード',    example: '150', notes: '会社内で一意 (閉店したコードは再利用可)' },
      { name: 'store_name',     required: true,  type: '文字列', description: '店舗名',        example: '目黒店' },
      { name: 'address',        required: false, type: '文字列', description: '住所',          example: '東京都目黒区...' },
      { name: 'effective_from', required: true,  type: '日付',   description: '有効開始日',    example: '2026/04/01' },
      { name: 'effective_to',   required: false, type: '日付',   description: '有効終了日',    example: '', notes: '未指定=無期限' },
      { name: 'status',         required: false, type: '文字列', description: '状態',          example: 'active', notes: 'active / closed のいずれか。未指定=active' },
      { name: 'operation_type', required: false, type: '文字列', description: '操作種別',      example: 'continue', notes: 'continue(実態継続)/renew(リニューアル・別店舗扱い)/close(閉店)/reopen(再開) のいずれか。未指定=continue。既存の6列CSVはそのまま動作' },
    ],
    commonNotes: [
      ...commonNotes,
      '**既存 store_code の再登録**は同 effective_from なら店舗名等が上書きされます (子データ整合は保持)',
      '**operation_type=renew**: 経営者交代・全面刷新等で「別店舗」として扱いたい場合に指定。既存店舗を閉店し、同じ store_code で新しい店舗として登録し直します',
      '**operation_type=close/reopen**: 閉店/再開を明示的に表す操作です。close は現在アクティブな店舗が対象、reopen は直近に閉店した店舗が対象になります',
      '**閉店は operation_type=close で指定**してください。status=closed だけの行は閉店になりません: 閉店済みの店舗の行 (店舗コードと開始日が一致) は変更がないため読み飛ばし、稼働中の店舗や一致しない行はエラーになります。店舗マスタ画面から出力した CSV をそのまま取込み直すことができます',
      '**reopen は閉店した店舗の元の親 (組織) の下に戻ります**。元の親が停止中、または同じ CSV で閉店する場合はエラーになります。開始日は閉店日以降で、これまでの履歴より後の日付を指定してください',
      '**renew の新しい店舗は、旧店舗と同じ親 (組織) の下に作られます**。稼働中の子ノード (店舗の下の店舗など) がある店舗は close / renew できません。親と子を両方閉じるときは、子を閉じる CSV と親を閉じる CSV の2回に分けて取込んでください',
      '**close / reopen / renew を指定した店舗コードは、CSV に1行しか書けません**。同じ店舗の履歴を複数行で登録するときは continue の行だけにし、effective_from の古い順に並べてください。新しい店舗コードは1行だけ書けます',
      '**effective_from は現在の版より前の日付にできません**',
      '**rollback 非対応**: マスタ系は取込後の自動巻き戻しができません。取込前に内容を必ず確認してください',
    ],
    rollbackSupported: false,
  },
  menu_categories: {
    key: 'menu_categories',
    label: 'メニューカテゴリマスタ',
    category: 'master',
    formatChangeNotice: '2026-07-31 に「カテゴリコード (category_code)」列が必須で追加されました。以前の形式のCSV (コード列なし) はエラーになります。お手元のテンプレートが古い場合は「テンプレートCSVをダウンロード」から取得し直してください。',
    summary: 'メニューのカテゴリ (ラーメン / ライス / トッピング / 一品料理 等)。(category_code, effective_from) をキーに upsert (v0.2〜: カテゴリ名ではなくコードが識別子)',
    columns: [
      { name: 'category_code',  required: true,  type: '文字列', description: 'カテゴリコード', example: 'CAT-005', notes: '半角英数字・ハイフン・アンダースコアのみ。識別子のため作成後の変更は不可 (コードを変えたい場合は別コードで新規登録)' },
      { name: 'category_name',  required: true,  type: '文字列', description: 'カテゴリ名',     example: 'サイドメニュー', notes: '表示用の名称。同じ category_code であれば変更可能' },
      { name: 'display_order',  required: false, type: '整数',   description: '表示順',         example: '50', notes: '未指定時は 0。ダッシュボードで昇順表示に使用' },
      { name: 'effective_from', required: true,  type: '日付',   description: '有効開始日',     example: '2026/04/01' },
      { name: 'effective_to',   required: false, type: '日付',   description: '有効終了日',     example: '', notes: '未指定=無期限' },
    ],
    commonNotes: [
      ...commonNotes,
      '**同一 (カテゴリコード, effective_from) が CSV 内に 2 行以上ある場合は取込前にエラー**',
      '**rollback 非対応**: マスタ系は取込後の自動巻き戻しができません',
    ],
    rollbackSupported: false,
  },
  ingredient_master: {
    key: 'ingredient_master',
    label: '食材マスタ',
    category: 'master',
    formatChangeNotice: '2026-07-31 に「食材コード (ingredient_code)」列が必須で追加されました。以前の形式のCSV (コード列なし) はエラーになります。お手元のテンプレートが古い場合は「テンプレートCSVをダウンロード」から取得し直してください。',
    summary: '食材マスタ。(store_code, ingredient_code, effective_from) をキーに upsert (v0.2〜: 食材名ではなくコードが識別子)。delivery_details 取込で ingredient_id / cost_category_id を自動紐付けに使用',
    columns: [
      { name: 'store_code',         required: true,  type: '文字列', description: '店舗コード',   example: '120' },
      { name: 'ingredient_code',    required: true,  type: '文字列', description: '食材コード',   example: 'ING-120-001', notes: '半角英数字・ハイフン・アンダースコアのみ。店舗内で一意、識別子のため作成後の変更は不可' },
      { name: 'ingredient_name',    required: true,  type: '文字列', description: '食材名',       example: '豚ゲンコツ', notes: '表示用の名称。同じ ingredient_code であれば変更可能 (履歴で複数バージョン管理可)' },
      { name: 'unit',               required: false, type: '文字列', description: '単位',         example: 'kg' },
      { name: 'unit_price',         required: false, type: '整数',   description: '単価(税抜)',   example: '220', notes: '目安。実際の単価は納品明細の unit_price を使用' },
      { name: 'cost_category_name', required: true,  type: '文字列', description: 'コスト分類名', example: 'ラーメン材料', notes: 'cost_categories.category_name と完全一致が必要。代表値: ラーメン材料 / スープ材料 / トッピング / 副材料 / 消耗品 (詳細はコスト分類マスタ画面で確認)' },
      { name: 'effective_from',     required: true,  type: '日付',   description: '有効開始日',   example: '2026/04/01' },
      { name: 'effective_to',       required: false, type: '日付',   description: '有効終了日',   example: '', notes: '未指定=無期限' },
    ],
    commonNotes: [
      ...commonNotes,
      '**cost_category_name 未登録エラー**: コスト分類マスタに存在しない名称を含む行はスキップされます。事前に登録するか行を修正してください',
      '**rollback 非対応**: マスタ系は取込後の自動巻き戻しができません',
    ],
    rollbackSupported: false,
  },
  delivery_services: {
    key: 'delivery_services',
    label: 'デリバリー業者マスタ',
    category: 'master',
    formatChangeNotice: '2026-07-31 に「業者コード (service_code)」列が必須で追加されました。以前の形式のCSV (コード列なし) はエラーになります。お手元のテンプレートが古い場合は「テンプレートCSVをダウンロード」から取得し直してください。',
    summary: '会社契約デリバリー業者の店舗別利用履歴。(store_code, service_code, effective_from) をキーに upsert (v0.2〜: 業者名ではなくコードが識別子)',
    columns: [
      { name: 'store_code',     required: true,  type: '文字列', description: '店舗コード',   example: '120' },
      { name: 'service_code',   required: true,  type: '文字列', description: '業者コード',   example: 'SVC-120-01', notes: '半角英数字・ハイフン・アンダースコアのみ。店舗内で一意、識別子のため作成後の変更は不可' },
      { name: 'service_name',   required: true,  type: '文字列', description: '業者名',       example: 'Uber Eats', notes: '表示用の名称。同じ service_code であれば変更可能' },
      { name: 'active_flg',     required: false, type: '真偽(0/1)', description: '有効フラグ', example: '1', notes: '未指定=1 (有効)' },
      { name: 'effective_from', required: true,  type: '日付',   description: '利用開始日',   example: '2024/04/01' },
      { name: 'effective_to',   required: false, type: '日付',   description: '利用終了日',   example: '', notes: '未指定=継続中' },
    ],
    commonNotes: [
      ...commonNotes,
      '**個別 CRUD 画面あり**: /masters/delivery-services で 1 件単位の追加・停止も可能',
      '**rollback 非対応**: マスタ系は取込後の自動巻き戻しができません',
    ],
    rollbackSupported: false,
  },
};

/** CSVテンプレートを生成 */
export const buildTemplateCsv = (spec: DataTypeSpec): string => {
  const header = spec.columns.map((c) => c.name).join(',');
  const example = spec.columns.map((c) => c.example).join(',');
  return `﻿${header}\r\n${example}\r\n`; // BOM付きUTF-8、CRLF改行
};

/** ダウンロード用にBlobを作ってダウンロードトリガーを発火 */
export const downloadCsv = (spec: DataTypeSpec) => {
  const csv = buildTemplateCsv(spec);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `template-${spec.key}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
